// Eskom PARCC — access-grant editor (admin).
//
// The right-hand pane of the Users screen. In ADD mode it captures a new email +
// role; in EDIT mode it works on the selected grant (email fixed). All writes go
// through the privileged admin-api hooks (server-side admin-claim enforced).
//
// Two distinct on-ramps, made explicit in the copy because they differ:
//   - "Grant access" records the email in the access list so an existing sign-in
//     (typically Google self-serve) is admitted. It does NOT create credentials.
//   - "Create credentials" provisions a brand-new email/password account and
//     returns a temporary password for the admin to convey out-of-band.
import { useState, type FormEvent } from 'react';
import { Button, Select } from '../../components/ui';
import { useGrantAccess, useRevokeAccess, useCreateCredentials, useSetPassword } from '../../hooks';
import { normalizeEmail } from '../../lib/email';
import type { Role } from '../../lib/access';
import { ROLES } from '../../domain/enums';
import { ROLE_LABELS } from '../../lib/permissions';
import { DIVISION_OPTIONS } from '../../domain/enums';
import type { AccessGrantRecord } from '../../lib/db';

/** Roles that belong to a single division (need a division picker). */
function isDivisionRole(role: Role): boolean {
  return role === 'teamLead' || role === 'reviewer';
}

type Status = { kind: 'ok' | 'bad'; message: string };

export function AccessGrantEditor({
  mode,
  grant,
  onClose,
}: {
  mode: 'add' | 'edit';
  /** The grant being edited; null in add mode. */
  grant: AccessGrantRecord | null;
  onClose: () => void;
}): React.JSX.Element {
  const [email, setEmail] = useState(grant?.email ?? '');
  const [firstName, setFirstName] = useState(grant?.firstName ?? '');
  const [lastName, setLastName] = useState(grant?.lastName ?? '');
  const [role, setRole] = useState<Role>((grant?.role as Role) ?? 'reviewer');
  const [division, setDivision] = useState(grant?.division ?? '');
  const [canAllocate, setCanAllocate] = useState(grant?.canAllocate ?? false);
  const [crossDivision, setCrossDivision] = useState(grant?.crossDivision ?? false);
  const [aiEnabled, setAiEnabled] = useState(grant?.aiEnabled ?? false);
  const [status, setStatus] = useState<Status | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  // Optional admin-typed password for the edit-mode "Set / reset password" action;
  // blank means "generate one server-side".
  const [newPassword, setNewPassword] = useState('');

  const grantAccess = useGrantAccess();
  const revoke = useRevokeAccess();
  const createCredentials = useCreateCredentials();
  const setPassword = useSetPassword();
  const busy =
    grantAccess.isPending ||
    revoke.isPending ||
    createCredentials.isPending ||
    setPassword.isPending;

  const isEdit = mode === 'edit';
  const isActive = grant?.active ?? false;

  // The displayName to persist: prefer the composed first/last, but fall back to
  // the grant's existing displayName so editing a legacy grant (which has only a
  // displayName, no structured name) never blanks it on a no-op save.
  function displayNameToSend(): string | null {
    const composed = [firstName, lastName]
      .map((s) => s.trim())
      .filter(Boolean)
      .join(' ');
    return composed || grant?.displayName || null;
  }

  // Division + crossDivision apply only to teamLead/reviewer; canAllocate only to
  // manager. The server re-applies the same invariant, so a stray value never persists.
  function permissionFields(): {
    division: string | null;
    canAllocate: boolean;
    crossDivision: boolean;
    aiEnabled: boolean;
  } {
    return {
      division: isDivisionRole(role) ? division.trim() || null : null,
      canAllocate: role === 'manager' ? canAllocate : false,
      crossDivision: isDivisionRole(role) ? crossDivision : false,
      // Role-independent: AI is a rollout switch, not a role capability.
      aiEnabled,
    };
  }

  function validEmail(): string | null {
    const normalised = normalizeEmail(email);
    if (!normalised || !normalised.includes('@')) {
      setStatus({ kind: 'bad', message: 'Enter a valid email address.' });
      return null;
    }
    return normalised;
  }

  /**
   * Reviewers and team leads MUST carry a home division: division-wide visibility
   * (register + dashboard) keys off it, and a blank one silently collapses them to
   * their own allocations only — the exact "no access" the R4 feedback flagged.
   * Block the save rather than persist a division-less scoped grant.
   */
  function divisionOk(): boolean {
    if (isDivisionRole(role) && division.trim() === '') {
      setStatus({
        kind: 'bad',
        message: 'Reviewers and team leads need a home division for division-wide visibility.',
      });
      return false;
    }
    return true;
  }

  async function handleGrant(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setStatus(null);
    const normalised = validEmail();
    if (!normalised) return;
    if (!divisionOk()) return;
    try {
      await grantAccess.mutateAsync({
        email: normalised,
        role,
        ...permissionFields(),
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        displayName: displayNameToSend(),
      });
      setStatus({
        kind: 'ok',
        message: isEdit
          ? 'Changes saved.'
          : 'Access granted. They’ll get a Firebase account when they first sign in — use “Create credentials” if you need a login now.',
      });
      if (!isEdit) onClose();
    } catch {
      // The hook's onError already toasts; keep the pane open.
    }
  }

  async function handleCreateCredentials(): Promise<void> {
    setStatus(null);
    setTempPassword(null);
    const normalised = validEmail();
    if (!normalised) return;
    if (!divisionOk()) return;
    try {
      const result = await createCredentials.mutateAsync({
        email: normalised,
        role,
        ...permissionFields(),
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        displayName: displayNameToSend(),
      });
      setTempPassword(result.tempPassword);
      setStatus({ kind: 'ok', message: 'Account created. Share the temporary password below.' });
    } catch {
      // toasted by the hook.
    }
  }

  // Set or reset the selected user's sign-in password directly (the admin
  // alternative to URL reset links). A blank field generates one server-side; a
  // typed one must meet the 8-character minimum.
  async function handleSetPassword(): Promise<void> {
    if (!grant) return;
    setStatus(null);
    setTempPassword(null);
    const typed = newPassword.trim();
    if (typed !== '' && typed.length < 8) {
      setStatus({ kind: 'bad', message: 'A typed password must be at least 8 characters.' });
      return;
    }
    try {
      const result = await setPassword.mutateAsync({
        email: grant.email,
        password: typed === '' ? undefined : typed,
      });
      setNewPassword('');
      setTempPassword(result.password);
      setStatus({
        kind: 'ok',
        message: result.created
          ? 'Account created and password set. Share the password below.'
          : 'Password reset. Share the new password below.',
      });
    } catch {
      // toasted by the hook.
    }
  }

  async function handleRevoke(): Promise<void> {
    if (!grant) return;
    setStatus(null);
    try {
      await revoke.mutateAsync(grant.email);
      setStatus({ kind: 'ok', message: 'Access revoked.' });
    } catch {
      // toasted by the hook.
    }
  }

  async function handleRestore(): Promise<void> {
    if (!grant) return;
    setStatus(null);
    try {
      await grantAccess.mutateAsync({
        email: grant.email,
        role,
        ...permissionFields(),
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        displayName: displayNameToSend(),
      });
      setStatus({ kind: 'ok', message: 'Access restored.' });
    } catch {
      // toasted by the hook.
    }
  }

  return (
    <div className="card">
      <div className="section-head">
        <div>
          <h3>{isEdit ? 'Edit access' : 'Add user'}</h3>
          <div className="small">
            {isEdit ? (
              'Update this user’s role or access.'
            ) : (
              <>
                Two ways to add someone: <strong>Grant access</strong> pre-authorises their email
                (they sign in with Google — no password) — or <strong>Create credentials</strong> to
                issue an email + password login now.
              </>
            )}
          </div>
        </div>
        <div className="actions">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Close
          </Button>
        </div>
      </div>

      <form onSubmit={(e) => void handleGrant(e)} noValidate>
        <label className="login-field" htmlFor="grantEmail">
          Email address
          <input
            id="grantEmail"
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy || isEdit}
            required
          />
        </label>

        <div className="detail-edit-grid" style={{ marginTop: 12 }}>
          <label className="login-field" htmlFor="grantFirstName">
            First name
            <input
              id="grantFirstName"
              type="text"
              autoComplete="off"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="login-field" htmlFor="grantLastName">
            Last name
            <input
              id="grantLastName"
              type="text"
              autoComplete="off"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={busy}
            />
          </label>
        </div>

        <label className="login-field" style={{ marginTop: 12 }}>
          Role
          <Select
            ariaLabel="Role"
            value={role}
            onChange={(v) => setRole(v as Role)}
            disabled={busy}
            options={ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] }))}
          />
        </label>

        {isDivisionRole(role) && (
          <label className="login-field" style={{ marginTop: 12 }}>
            Division
            <Select
              ariaLabel="Division"
              value={division}
              onChange={setDivision}
              disabled={busy}
              options={[
                { value: '', label: '— Select a division —' },
                ...DIVISION_OPTIONS.map((d) => ({ value: d, label: d })),
              ]}
            />
          </label>
        )}

        {role === 'manager' && (
          <label
            className="small"
            htmlFor="grantCanAllocate"
            style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <input
              id="grantCanAllocate"
              type="checkbox"
              checked={canAllocate}
              onChange={(e) => setCanAllocate(e.target.checked)}
              disabled={busy}
            />
            May allocate transactions (across all divisions)
          </label>
        )}

        {isDivisionRole(role) && (
          <label
            className="small"
            htmlFor="grantCrossDivision"
            style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <input
              id="grantCrossDivision"
              type="checkbox"
              checked={crossDivision}
              onChange={(e) => setCrossDivision(e.target.checked)}
              disabled={busy}
            />
            See all divisions (cross-division helper — keeps their own edit rights)
          </label>
        )}

        {/* AI eligibility. Role-independent and rolled out one person at a time,
            so it sits outside the role-conditional blocks above. Granting it only
            makes the switch AVAILABLE — the person still turns AI on themselves
            in Settings, so nobody is opted in on their behalf. */}
        <label
          className="small"
          htmlFor="grantAiEnabled"
          style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <input
            id="grantAiEnabled"
            type="checkbox"
            checked={aiEnabled}
            onChange={(e) => setAiEnabled(e.target.checked)}
            disabled={busy}
          />
          Allow the AI Compliance Engine (they switch it on themselves in Settings)
        </label>

        {status && (
          <div
            className={status.kind === 'bad' ? 'callout' : 'small'}
            role={status.kind === 'bad' ? 'alert' : 'status'}
            style={{ marginTop: 12 }}
          >
            {status.message}
          </div>
        )}

        {tempPassword && (
          <div className="login-note" style={{ marginTop: 12 }}>
            Password (convey this securely; the user should change it from “Change password” after
            signing in):
            <code style={{ display: 'block', marginTop: 6, userSelect: 'all', fontWeight: 800 }}>
              {tempPassword}
            </code>
          </div>
        )}

        {isEdit && (
          <label className="login-field" htmlFor="newPassword" style={{ marginTop: 12 }}>
            New password (optional)
            <input
              id="newPassword"
              type="text"
              autoComplete="off"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={busy}
              placeholder="Leave blank to generate one"
            />
            <span className="small">
              Sets or resets this user’s sign-in password directly — no email link needed. Leave
              blank to auto-generate one.
            </span>
          </label>
        )}

        {!isEdit && (
          <div className="login-note" style={{ marginTop: 12 }}>
            <strong>Grant access</strong> — adds this email to the access list. They appear under
            Firebase Authentication only after their first sign-in; no password is created.
            <br />
            <strong>Create credentials</strong> — creates a login immediately and shows a temporary
            password to share.
          </div>
        )}

        <div className="actions" style={{ marginTop: 16, gap: 8, flexWrap: 'wrap' }}>
          {isEdit ? (
            <>
              {isActive ? (
                <Button type="submit" variant="primary" disabled={busy}>
                  {grantAccess.isPending ? 'Saving…' : 'Save changes'}
                </Button>
              ) : (
                <Button variant="primary" onClick={() => void handleRestore()} disabled={busy}>
                  {grantAccess.isPending ? 'Restoring…' : 'Restore access'}
                </Button>
              )}

              {/* Idempotent — sets or resets the existing user's password. Replaces
                  "Create credentials" here, which 409'd on an account that exists. */}
              <Button onClick={() => void handleSetPassword()} disabled={busy}>
                {setPassword.isPending ? 'Setting…' : 'Set / reset password'}
              </Button>

              {isActive && (
                <Button className="danger" onClick={() => void handleRevoke()} disabled={busy}>
                  {revoke.isPending ? 'Revoking…' : 'Revoke access'}
                </Button>
              )}
            </>
          ) : (
            <>
              <Button type="submit" variant="primary" disabled={busy}>
                {grantAccess.isPending ? 'Granting…' : 'Grant access'}
              </Button>
              {/* New account only — provisions credentials and shows a temp password. */}
              <Button onClick={() => void handleCreateCredentials()} disabled={busy}>
                {createCredentials.isPending ? 'Creating…' : 'Create credentials'}
              </Button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}
